// Read a saved email (.eml — Gmail: ⋮ → "Download message") into the fields a paper trail
// needs: who sent it, to whom, when, the subject, and the text. Pure, no dependencies, so it
// runs in the browser and in Node tests alike.
//
// Deliberately header-exact: the sender, the Date header and the Subject are copied from the
// message itself, not retyped — "who told us, and when" is the whole point of keeping it.

const decoderFor = (charset) => {
  try { return new TextDecoder((charset || 'utf-8').toLowerCase()); } catch (e) { return new TextDecoder('utf-8'); }
};

function bytesFromBinary(s) {
  const b = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) b[i] = s.charCodeAt(i) & 0xff;
  return b;
}

function b64ToBytes(s) {
  const clean = (s || '').replace(/[^A-Za-z0-9+/=]/g, '');
  if (typeof atob === 'function') return bytesFromBinary(atob(clean));
  return new Uint8Array(Buffer.from(clean, 'base64'));
}

function qpToBytes(s, header) {
  let t = s || '';
  if (header) t = t.replace(/_/g, ' ');
  t = t.replace(/=\r?\n/g, '');
  const out = [];
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (c === '=' && /^[0-9A-F]{2}$/i.test(t.substr(i + 1, 2))) { out.push(parseInt(t.substr(i + 1, 2), 16)); i += 2; }
    else out.push(t.charCodeAt(i) & 0xff);
  }
  return new Uint8Array(out);
}

// "=?utf-8?Q?Re=3A_CBS?=" → "Re: CBS". Adjacent encoded words join without the space.
export function decodeWords(s) {
  return (s || '')
    .replace(/\?=\s+=\?/g, '?==?')
    .replace(/=\?([^?]+)\?([BQbq])\?([^?]*)\?=/g, (m, cs, enc, txt) => {
      try { return decoderFor(cs).decode(enc.toUpperCase() === 'B' ? b64ToBytes(txt) : qpToBytes(txt, true)); }
      catch (e) { return m; }
    });
}

function splitHead(raw) {
  const m = raw.match(/\r?\n\r?\n/);
  if (!m) return { head: raw, body: '' };
  return { head: raw.slice(0, m.index), body: raw.slice(m.index + m[0].length) };
}

function parseHeaders(head) {
  const out = {};
  const lines = head.replace(/\r?\n[ \t]+/g, ' ').split(/\r?\n/);
  for (const l of lines) {
    const i = l.indexOf(':');
    if (i <= 0) continue;
    const k = l.slice(0, i).trim().toLowerCase();
    const v = l.slice(i + 1).trim();
    if (!(k in out)) out[k] = v;   // first wins: the top-most header is the message's own
  }
  return out;
}

const param = (h, name) => {
  const m = (h || '').match(new RegExp(`${name}\\s*=\\s*("([^"]*)"|[^;\\s]+)`, 'i'));
  return m ? (m[2] != null ? m[2] : m[1]) : '';
};

function decodeBody(body, headers) {
  const cte = (headers['content-transfer-encoding'] || '').toLowerCase();
  const cs = param(headers['content-type'], 'charset') || 'utf-8';
  if (cte === 'base64') return decoderFor(cs).decode(b64ToBytes(body));
  if (cte === 'quoted-printable') return decoderFor(cs).decode(qpToBytes(body));
  return body;
}

const htmlToText = (h) => (h || '')
  .replace(/<(style|script)[\s\S]*?<\/\1>/gi, '')
  .replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|tr|li|h\d)>/gi, '\n')
  .replace(/<[^>]+>/g, '')
  .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
  .replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();

// Walk a MIME tree for the best text: text/plain first, html stripped as the fallback.
function findText(part) {
  const { head, body } = splitHead(part);
  const h = parseHeaders(head);
  const ct = (h['content-type'] || 'text/plain').toLowerCase();
  if (ct.startsWith('multipart/')) {
    const b = param(h['content-type'], 'boundary');
    if (!b) return { plain: '', html: '' };
    const parts = body.split(new RegExp(`\\r?\\n?--${b.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:--)?\\s*`)).filter((p) => p.trim());
    const acc = { plain: '', html: '' };
    for (const p of parts) {
      const r = findText(p);
      if (!acc.plain && r.plain) acc.plain = r.plain;
      if (!acc.html && r.html) acc.html = r.html;
    }
    return acc;
  }
  if (/attachment/i.test(h['content-disposition'] || '')) return { plain: '', html: '' };
  if (ct.startsWith('text/plain')) return { plain: decodeBody(body, h), html: '' };
  if (ct.startsWith('text/html')) return { plain: '', html: htmlToText(decodeBody(body, h)) };
  return { plain: '', html: '' };
}

export function splitAddress(v) {
  const s = decodeWords(v || '').trim();
  const m = s.match(/^\s*"?([^"<]*?)"?\s*<([^>]+)>/);
  if (m) return { name: m[1].trim(), email: m[2].trim() };
  return { name: '', email: s.replace(/[<>]/g, '').trim() };
}

export function parseEml(raw) {
  const text = typeof raw === 'string' ? raw : '';
  const { head } = splitHead(text);
  const h = parseHeaders(head);
  const from = splitAddress(h.from);
  const d = h.date ? new Date(h.date) : null;
  const t = findText(text);
  return {
    from_name: from.name,
    from_email: from.email,
    to: [decodeWords(h.to || ''), h.cc ? `cc ${decodeWords(h.cc)}` : ''].filter(Boolean).join(' · '),
    sent_at: d && !isNaN(d) ? d.toISOString() : '',
    subject: decodeWords(h.subject || ''),
    body: (t.plain || t.html || '').replace(/\r\n/g, '\n').trim(),
  };
}
